/**
 * Utility functions for formatting log messages in a user-friendly way
 */

export interface LogPayload {
    message?: string;
    status?: number;
    method?: string;
    url?: string;
    payload?: any;
    error?: string;
    [key: string]: any;
}

/**
 * Formats a log message from various payload formats
 */
export function formatLogMessage(payload: LogPayload | string): string {
    // If payload is already a string, try to parse it
    if (typeof payload === 'string') {
        try {
            const parsed = JSON.parse(payload);
            return formatLogMessage(parsed);
        } catch {
            // If it's not JSON, return as is
            return payload || 'No message provided';
        }
    }

    // If payload has a direct message, use it
    if (payload.message) {
        return payload.message;
    }

    // Try to extract message from nested payload
    if (payload.payload) {
        if (typeof payload.payload === 'string') {
            try {
                const parsedPayload = JSON.parse(payload.payload);
                if (parsedPayload.message) {
                    return parsedPayload.message;
                }
                return formatLogMessage(parsedPayload);
            } catch {
                return payload.payload;
            }
        }
        if (payload.payload.message) {
            return payload.payload.message;
        }
    }

    // Format based on error status
    if (payload.isError || payload.error) {
        return formatErrorMessage(payload);
    }

    // Format based on HTTP method and URL
    if (payload.method && payload.url) {
        return formatHttpAction(payload);
    }

    // Try to format from object structure
    if (typeof payload === 'object') {
        return formatObjectMessage(payload);
    }

    return 'No message provided';
}

/**
 * Formats error messages in a user-friendly way
 */
function formatErrorMessage(payload: LogPayload): string {
    const errorMsg = payload.error || payload.message || 'An error occurred';

    // Common error patterns
    if (errorMsg.includes('Account Id already exists')) {
        return `Failed to add performance account: Account ID already exists`;
    }

    if (errorMsg.includes('already exists')) {
        return `Operation failed: ${errorMsg}`;
    }

    if (errorMsg.includes('Error while')) {
        return errorMsg;
    }

    // Extract meaningful error from various formats
    if (payload.payload) {
        const payloadStr = typeof payload.payload === 'string'
            ? payload.payload
            : JSON.stringify(payload.payload);

        if (payloadStr.includes('Error')) {
            const errorMatch = payloadStr.match(/Error[^:]*:\s*(.+?)(?:"|$|,)/i);
            if (errorMatch && errorMatch[1]) {
                return `Error: ${errorMatch[1].trim()}`;
            }
        }
    }

    return `Error: ${errorMsg}`;
}

/**
 * URL patterns to human-readable resource names
 */
const URL_RESOURCE_MAP: Array<{ pattern: RegExp; name: string; extractName?: boolean }> = [
    // Organizations
    { pattern: /\/organizations?\/(\d+)/, name: 'organization', extractName: true },
    { pattern: /\/organizations?$/, name: 'organization' },
    // Brands
    { pattern: /\/brands?\/update\/(\d+)/, name: 'brand', extractName: true },
    { pattern: /\/brands?\/(\d+)/, name: 'brand', extractName: true },
    { pattern: /\/brands?$/, name: 'brand' },
    // Users
    { pattern: /\/admin\/verify\/(\d+)/, name: 'user verification', extractName: true },
    { pattern: /\/admin\/hold\/(\d+)/, name: 'user hold status', extractName: true },
    { pattern: /\/admin\/delete\/(\d+)/, name: 'user', extractName: true },
    { pattern: /\/admin\/team\/(\d+)/, name: 'user team status', extractName: true },
    { pattern: /\/admin\/level\/(\d+)/, name: 'user admin level', extractName: true },
    { pattern: /\/admin\/superadmin\/(\d+)/, name: 'superadmin status', extractName: true },
    { pattern: /\/users?\/(\d+)/, name: 'user', extractName: true },
    // Permissions
    { pattern: /\/product_permissions\/users\/(\d+)\/permissions/, name: 'user permissions', extractName: true },
    { pattern: /\/product_permissions\/groups\/(\d+)\/permissions/, name: 'group permissions', extractName: true },
    { pattern: /\/product_permissions\/groups\/(\d+)\/members/, name: 'group members', extractName: true },
    { pattern: /\/product_permissions\/groups\/(\d+)/, name: 'permission group', extractName: true },
    { pattern: /\/product_permissions\/groups/, name: 'permission group' },
    { pattern: /\/user_permissions\/(\d+)/, name: 'user permissions', extractName: true },
    // GA4
    { pattern: /\/ga4Values\/(\d+)/, name: 'GA4 configuration', extractName: true },
    { pattern: /\/ga4Values/, name: 'GA4 configuration' },
    // Reports
    { pattern: /\/report-config\/configurations\/(\d+)/, name: 'report configuration', extractName: true },
    { pattern: /\/report-config\/configurations/, name: 'report configuration' },
    // Drawer
    { pattern: /\/default-items\/([a-zA-Z0-9-]+)/, name: 'default sidebar item', extractName: true },
    { pattern: /\/default-items/, name: 'default sidebar item' },
    { pattern: /\/items\/([a-zA-Z0-9-]+)/, name: 'drawer item', extractName: true },
    { pattern: /\/items/, name: 'drawer item' },
    // Buddies/AI
    { pattern: /\/buddies\/([a-zA-Z0-9-]+)\/files/, name: 'buddy files' },
    { pattern: /\/buddies\/([a-zA-Z0-9-]+)/, name: 'AI buddy', extractName: true },
    { pattern: /\/buddies/, name: 'AI buddy' },
    // Model config
    { pattern: /\/model-config\/(\w+)/, name: 'model configuration', extractName: true },
    { pattern: /\/model-config/, name: 'model configuration' },
    { pattern: /\/models-registry\/([a-zA-Z0-9-_.]+)/, name: 'model registry entry', extractName: true },
    { pattern: /\/models-registry/, name: 'model registry' },
    // Master memories
    { pattern: /\/master-memories\/single\/([a-zA-Z0-9-]+)/, name: 'master memory', extractName: true },
    { pattern: /\/master-memories\/([a-zA-Z0-9-]+)/, name: 'user memories' },
    { pattern: /\/master-memories/, name: 'master memory' },
    // Workflows
    { pattern: /\/workflows\/([a-zA-Z0-9-]+)/, name: 'workflow', extractName: true },
    { pattern: /\/workflows/, name: 'workflow' },
    // Performance accounts
    { pattern: /\/accounts?\/(\d+)/, name: 'performance account', extractName: true },
    { pattern: /\/accounts?/, name: 'performance account' },
    // Taxonomy
    { pattern: /\/txnmy\/cdvalue/, name: 'taxonomy value' },
    { pattern: /\/txnmy/, name: 'taxonomy' },
];

/**
 * Formats HTTP action messages with smart resource detection
 */
function formatHttpAction(payload: LogPayload): string {
    const method = payload.method?.toUpperCase() || 'REQUEST';
    const url = payload.url || 'unknown endpoint';

    // Try to match against known URL patterns
    for (const { pattern, name, extractName } of URL_RESOURCE_MAP) {
        const match = url.match(pattern);
        if (match) {
            const resourceId = extractName && match[1] ? ` (ID: ${match[1]})` : '';
            
            // Try to get name from payload if available
            let resourceName = '';
            if (payload.name) {
                resourceName = ` "${payload.name}"`;
            } else if (payload.payload?.name) {
                resourceName = ` "${payload.payload.name}"`;
            }
            
            switch (method) {
                case 'GET':
                    return `Retrieved ${name}${resourceName}${resourceId}`;
                case 'POST':
                    return `Created ${name}${resourceName}${resourceId}`;
                case 'PUT':
                case 'PATCH':
                    return `Updated ${name}${resourceName}${resourceId}`;
                case 'DELETE':
                    return `Deleted ${name}${resourceName}${resourceId}`;
                default:
                    return `${method} ${name}${resourceName}${resourceId}`;
            }
        }
    }

    // Fallback: extract meaningful parts from URL
    const urlParts = url.split('/').filter(Boolean);
    
    // Find the last non-numeric, non-version segment
    let endpoint = 'resource';
    for (let i = urlParts.length - 1; i >= 0; i--) {
        const part = urlParts[i];
        // Skip if it's a number (ID), version string, or common prefixes
        if (!/^\d+$/.test(part) && !/^v\d+$/.test(part) && !['admin', 'api', 'user', 'brand'].includes(part)) {
            endpoint = part.replace(/-/g, ' ').replace(/_/g, ' ');
            break;
        }
    }

    // Format based on method
    switch (method) {
        case 'GET':
            return `Retrieved ${endpoint}`;
        case 'POST':
            return `Created ${endpoint}`;
        case 'PUT':
        case 'PATCH':
            return `Updated ${endpoint}`;
        case 'DELETE':
            return `Deleted ${endpoint}`;
        default:
            return `${method} ${endpoint}`;
    }
}

/**
 * Formats messages from object structures
 */
function formatObjectMessage(payload: LogPayload): string {
    // Check for common action patterns
    if (payload.name) {
        if (payload.action === 'add' || payload.action === 'created') {
            return `Added ${payload.type || 'item'}: "${payload.name}"`;
        }
        if (payload.action === 'update' || payload.action === 'updated') {
            return `Updated ${payload.type || 'item'}: "${payload.name}"`;
        }
        if (payload.action === 'delete' || payload.action === 'deleted') {
            return `Deleted ${payload.type || 'item'}: "${payload.name}"`;
        }
        return `${payload.type || 'Item'}: "${payload.name}"`;
    }

    // Try to extract meaningful information
    const keys = Object.keys(payload).filter(key =>
        !['isError', 'status', 'method', 'url', 'payload', 'error'].includes(key)
    );

    if (keys.length > 0 && keys[0]) {
        const firstKey = keys[0];
        const value = payload[firstKey];
        return `${firstKey}: ${typeof value === 'object' ? JSON.stringify(value) : value}`;
    }

    return 'Action performed';
}

/**
 * Formats login/logout messages
 */
export function formatAuthMessage(action: 'login' | 'logout', email: string): string {
    return action === 'login'
        ? `User logged in: ${email}`
        : `User logged out: ${email}`;
}

/**
 * Formats drawer/report action messages
 */
export function formatDrawerActionMessage(
    action: 'add' | 'update' | 'delete',
    itemType: 'report' | 'item' | 'drawer',
    itemName: string,
    additionalInfo?: string
): string {
    const actionVerb = action === 'add' ? 'Added' : action === 'update' ? 'Updated' : 'Deleted';
    const typeLabel = itemType === 'report' ? 'report' : itemType === 'drawer' ? 'drawer structure' : 'item';

    let message = `${actionVerb} ${typeLabel}`;

    if (itemName) {
        try {
            const nameObj = typeof itemName === 'string' ? JSON.parse(itemName) : itemName;
            const name = nameObj.name || nameObj.display_name || JSON.stringify(nameObj);
            message += ` "${name}"`;
        } catch {
            message += ` "${itemName}"`;
        }
    }

    if (additionalInfo) {
        message += ` ${additionalInfo}`;
    }

    return message;
}

/**
 * Formats performance account messages
 */
export function formatPerformanceAccountMessage(
    action: 'add' | 'update' | 'delete',
    accountId?: string,
    error?: string
): string {
    if (error) {
        return `Failed to ${action} performance account${accountId ? ` (ID: ${accountId})` : ''}: ${error}`;
    }

    const actionVerb = action === 'add' ? 'Added' : action === 'update' ? 'Updated' : 'Deleted';
    return `${actionVerb} performance account${accountId ? ` (ID: ${accountId})` : ''}`;
}

/**
 * Formats taxonomy messages
 */
export function formatTaxonomyMessage(
    action: 'create' | 'update' | 'delete',
    taxonomyType: string,
    name: string,
    details?: string[]
): string {
    const actionVerb = action === 'create' ? 'Created' : action === 'update' ? 'Updated' : 'Deleted';
    let message = `${actionVerb} taxonomy ${taxonomyType}: "${name}"`;

    if (details && details.length > 0) {
        message += ` (${details.join(', ')})`;
    }

    return message;
}

